// This file contains logic around fetching the unsampled last rows of run histories.
// We need to know the max step (final step with media data) to configure the step slider maximum.
// If all we have is sampled data, we have no way of knowing what the actual max step is (it may not have been sampled).
// So we refetch the last rows while avoiding any sampling (by requesting a relatively small range of steps).

import produce from 'immer';
import _ from 'lodash';
import {useEffect, useRef, useState} from 'react';

import {Data, Query} from '../../../state/runs/types';
import {fetchUnsampledRunHistory} from './history';

type UseAugmentedDataParams = {
  data: Data;
  query: Query;
};

export function useAugmentedData({data, query}: UseAugmentedDataParams): Data {
  const [augmentedData, setAugmentedData] = useState<Data>(data);
  const lastRequestIDRef = useRef<number | null>(null);

  // Whenever new history data comes in, set it as the source of truth, then augment it with unsampled last steps.
  useEffect(() => {
    if (!data.loading) {
      setAugmentedData(data);
    }
    (async () => {
      const requestID = Math.random();
      lastRequestIDRef.current = requestID;

      const {histories, filteredRunsById} = data;
      const promises: Array<Promise<void>> = [];
      let aug = data;

      for (let i = 0; i < histories.data.length; i++) {
        const r = histories.data[i];
        const iConst = i;

        const maxSampledStep: number | null =
          _.maxBy(r.history, h => h._step)?._step ?? null;
        const sampleSize = query.historySpecs?.[0]?.samples;
        if (
          maxSampledStep == null ||
          sampleSize == null ||
          maxSampledStep < sampleSize
        ) {
          // We don't have enough steps to even trigger sampling. Ignore this entry.
          continue;
        }

        const historyKeys = query.historySpecs?.[0]?.keys;
        if (historyKeys == null) {
          continue;
        }

        const run = filteredRunsById[r.name];
        const runsetID = run.runsetInfo.id;
        const runsetQuery = _.find(query.queries, q => q.id === runsetID);
        if (runsetQuery == null) {
          throw new Error(
            'Inconsistent data from the server, run does not have a matching runset'
          );
        }
        const {entityName, projectName} = runsetQuery;

        // Start an async process to fetch the unsampled last steps, then
        // immutably update history data with the fetched steps.
        promises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            const unsampledHistory = await fetchUnsampledRunHistory({
              runSignature: {entityName, projectName, runName: r.name},
              historyKeys,
              minStep: maxSampledStep + 1,
            });
            if (unsampledHistory == null) {
              return;
            }
            // Immutably append fetched steps.
            // Mutating would break memoization around history data.
            aug = produce(aug, draft => {
              draft.histories.data[iConst].history.push(...unsampledHistory);
            });
          })()
        );
      }

      // No augmentations in flight. Finish.
      if (promises.length === 0) {
        return;
      }

      // Wait until all the fetching and appending is done,
      // then set the immutably-updated reference as the source of truth.
      await Promise.all(promises);
      if (requestID === lastRequestIDRef.current) {
        setAugmentedData(aug);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return augmentedData;
}
